import "./App.css";
import { Loader } from "./components";
import { Artist, QRCodePage } from "./pages";
import { Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { StaticService } from "./services";
import { ArtistProvider } from "./contexts/ArtistContext";

function App() {
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const claimSessionId = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const i = parts.findIndex((p) => p.toLowerCase() === "claim");
    return i >= 0 && parts[i + 1] ? parts[i + 1] : null;
  }, [location.pathname]);

  // QR Code validation states
  const [qrValidation, setQrValidation] = useState({
    isChecking: false,
    isValid: true,
    data: null,
    error: null,
    qrId: null,
  });

  // ==========================================
  // PRELOADED DATA STATES
  // ==========================================
  const [preloadedData, setPreloadedData] = useState({
    carouselData: null,
    teamMembers: null,
    newsItems: null,
  });

  const [preloadErrors, setPreloadErrors] = useState({
    carousel: null,
    aboutImage: null,
    newsItems: null,
  });

  // Session ID extracted from URL
  const [sessionId, setSessionId] = useState("default");

  // Nickname state for QR code pages
  const [nickname, setNickname] = useState(() => {
    // Try to get nickname from sessionStorage on init
    if (sessionId) {
      const stored = sessionStorage.getItem(`nickname_${sessionId}`);
      return stored || "Nuno";
    }
    return "Nuno";
  });

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Check if we're on the QR code page (to hide header)
  const isQrCodePage = location.pathname.includes("/qrcode");
  const isClaimPage = location.pathname.includes("/claim");

  // Extract QR code ID from URL
  const qrId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts[1] === "qrcode" && pathParts[2]) {
      return pathParts[2];
    }
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("session");
  }, [location.pathname, location.search]);

  /*/ Extract claim session ID from URL
  const claimSessionId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts[1] === "claim" && pathParts[2]) {
      return pathParts[2];
    }
    return null;
  }, [location.pathname]);*/

  // ==========================================
  // PRELOAD COMMON DATA ON APP STARTUP
  // ==========================================

  useEffect(() => {
    const preLoadAppData = async () => {
      const startTime = performance.now();

      try {
        console.log("Starting optimized app data preload...");

        // Use the new batch fetch method - single call for all data
        const result = await StaticService.batchFetchAppData();

        // Preload brand images, not being used at the moment
        // loadPromises.push(
        //   StaticService.getBrandImages()
        //     .then((images) => ({ type: "brandImages", data: images }))
        //     .catch((error) => {
        //       console.error("Failed to preload about images:", error);
        //       loadErrors.aboutImages = error.message;
        //       return { type: "brandImages", data: [] };
        //     }),
        // );

        //Preload Instagram posts
        // loadPromises
        //   .push(StaticService.fetchInstagramPosts())
        //   .then((posts) => ({ type: "instagramPosts", data: posts }))
        //   .catch((error) => {
        //     console.error("Failed to preload Instgram posts:", error);
        //     loadErrors.instagramPosts = error.message;
        //     return { type: "instagramPosts", data: null };
        //   });

        // Update state once with all data
        setPreloadedData({
          carouselData: result.carouselData,
          teamMembers: result.teamMembers,
          newsItems: result.newsItems,
        });

        setPreloadErrors(result.errors);

        const endTime = performance.now();
        console.log(
          `App data preload completed in ${(endTime - startTime).toFixed(2)}ms`,
        );
      } catch (error) {
        console.error("Unexpected error during app data preloading:", error);
        // Set empty data on error to allow app to continue
        setPreloadedData({
          carouselData: null,
          teamMembers: [],
          newsItems: [],
        });
      }
    };
    preLoadAppData();
  }, []);

  // ==========================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // ==========================================
  // Update sessionId when on claim page
  useEffect(() => {
    if (claimSessionId) {
      setSessionId(claimSessionId);
    }
  }, [claimSessionId]);

  // Handle QR code validation when on QR code pages
  // In App.js, replace entire validation useEffect:
  useEffect(() => {
    if (isQrCodePage) {
      if (!qrId) {
        setQrValidation({
          isChecking: false,
          isValid: true,
          data: {
            name: "TFNCA Session",
            description: "Session",
          },
          error: null,
          qrId: null,
        });
        setSessionId("default");
        return;
      }

      setSessionId(qrId);

      // Trust all session IDs - no validation needed
      setQrValidation({
        isChecking: false,
        isValid: true,
        data: {
          name: "TFNCA Session",
          description: "Session",
        },
        error: null,
        qrId,
      });
    } else {
      // Reset when not on QR page
      setQrValidation({
        isChecking: false,
        isValid: false,
        data: null,
        error: null,
        qrId: null,
      });
      setSessionId("default");
    }
  }, [isQrCodePage, qrId]);

  useEffect(() => {
    if (sessionId && sessionId !== "default") {
      const stored = sessionStorage.getItem(`nickname_${sessionId}`);
      if (stored && stored !== nickname) {
        setNickname(stored);
      }
    }
  }, [nickname, sessionId]);

  // Function to be called by the Loader when animation completes
  const handleLoaderComplete = () => {
    console.log("Loader animation completed");
    setLoading(false);
  };

  // Function to handle nickname submission, called from Loader
  const handleNicknameSubmit = (submittedNickname) => {
    console.log("Nickname submitted:", submittedNickname);
    setNickname(submittedNickname);

    // Persist to sessionStorage with session-specific key
    if (sessionId) {
      sessionStorage.setItem(`nickname_${sessionId}`, submittedNickname);
    }

    setLoading(false);
  };

  // Determine what to show based on current state
  const shouldShowLoader = () => {
    if (isQrCodePage || isClaimPage) {
      // Don't show loader if we have a nickname (already claimed)
      if (nickname) return false;

      // Show loader during validation or if valid but still loading
      return qrValidation.isChecking || (qrValidation.isValid && loading);
    }
    return loading;
  };

  return (
    <div className={`app-container ${isHomePage ? "home-page" : ""}`}>
      {shouldShowLoader() ? (
        <Loader
          onComplete={handleLoaderComplete}
          isQrCodePage={isQrCodePage}
          onNicknameSubmit={handleNicknameSubmit}
          sessionId={sessionId}
        />
      ) : (
        <>
          <Routes>
            <Route
              //changed pathname to / instead of /artist
              path="/"
              element={
                <ArtistProvider
                  initialGroups={preloadedData.carouselData?.groupNames || []}
                >
                  <Artist
                    carouselData={preloadedData.carouselData}
                    errorMessage={preloadErrors.carousel}
                    preloadedTeamMembers={preloadedData.teamMembers}
                    teamError={preloadErrors.teamMembers}
                    preloadedNews={preloadedData.newsItems}
                    newsError={preloadErrors.newsItems}
                  />
                </ArtistProvider>
              }
            />
            {/* NEW ROUTE: Claim reserved session */}
            <Route
              path="/claim/:id"
              element={
                <Loader
                  onComplete={handleLoaderComplete}
                  isQrCodePage={true}
                  onNicknameSubmit={handleNicknameSubmit}
                  sessionId={claimSessionId}
                  isClaimMode={true}
                />
              }
            />
            {/* QR Code routes with validated data and sessionId */}
            <Route
              path="/qrcode/:id?"
              element={
                <QRCodePage
                  //Brand images are not being used at the moment
                  // preloadedImages={preloadedData.brandImages}
                  // imagesError={preloadErrors.brandImages}
                  preloadedImages={[]}
                  imagesError={null}
                  qrValidation={qrValidation}
                  nickname={nickname}
                  sessionId={sessionId}
                />
              }
            />
          </Routes>
        </>
      )}
    </div>
  );
}

export default App;
